/**
 * Helper phân loại & sắp xếp nhóm công đoạn in ấn & bao bì
 * Chuẩn ISO 12647 / Industrial Process Stages
 */

export type ProcessGroupKey =
  | 'TRUOC_IN'
  | 'IN'
  | 'SAU_IN'
  | 'THANH_PHAM'
  | 'KHAC';

export const PROCESS_GROUP_ORDER: Record<ProcessGroupKey, number> = {
  TRUOC_IN: 0,
  IN: 1,
  SAU_IN: 2,
  THANH_PHAM: 3,
  KHAC: 4,
};

/**
 * Loại bỏ dấu tiếng Việt để so khớp từ khóa không dấu trong dữ liệu thực tế
 */
export function removeAccents(str: string): string {
  return (str || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[đĐ]/g, 'd')
    .toLowerCase();
}

/**
 * Phân loại nhóm thông minh dựa trên group sẵn có hoặc từ khóa tên / loại máy / mã CĐ
 */
export function resolveProcessGroup(item?: {
  group?: string | null;
  name?: string | null;
  defaultMachineType?: string | null;
  stepId?: string | null;
} | null): ProcessGroupKey {
  if (!item) return 'KHAC';

  const name = (item.name || '').toLowerCase();
  const machine = (item.defaultMachineType || '').toLowerCase();
  const stepId = (item.stepId || '').toLowerCase();
  const text = `${name} ${machine} ${stepId}`;
  const norm = removeAccents(text);

  // 1. Kiểm tra trường group đã gán (nếu hợp lệ và không xung đột với từ khóa rõ ràng)
  if (item.group) {
    const g = item.group.toUpperCase().trim();
    if (
      [
        'TRUOC_IN',
        'TRUOC IN',
        'PRE_PRESS',
        'PRE PRESS',
        'GHEP_MANG',
        'GHEP MANG',
        'LAMINATING',
        'GHÉP MÀNG',
      ].includes(g)
    ) {
      return 'TRUOC_IN';
    }
    if (
      ['IN', 'IN AN', 'IN_AN', 'PRINTING', 'OFFSET', 'FLEXO'].includes(g) &&
      !name.includes('thành phẩm') &&
      !norm.includes('thanh pham') &&
      !stepId.includes('finish')
    ) {
      return 'IN';
    }
    if (
      [
        'SAU_IN',
        'SAU IN',
        'POST_PRESS',
        'POST PRESS',
        'SURFACE',
        'FINISHING_SURFACE',
      ].includes(g)
    ) {
      return 'SAU_IN';
    }
    if (
      [
        'THANH_PHAM',
        'THANH PHAM',
        'CONVERTING',
        'PACKAGING',
        'FINISHING',
        'DONG_GOI',
      ].includes(g)
    ) {
      return 'THANH_PHAM';
    }
  }

  // 2. Phân tích văn bản (Name, Default Machine Type, Step ID - hỗ trợ cả có dấu và không dấu)

  // 2.1. Trước in / Chuẩn bị
  if (
    norm.includes('xen giay') ||
    norm.includes('cat xen') ||
    norm.includes('cat giay') ||
    norm.includes('xen') ||
    norm.includes('ghep mang') ||
    norm.includes('laminat') ||
    norm.includes('kem') ||
    norm.includes('ctp') ||
    norm.includes('thiet ke') ||
    norm.includes('binh ban') ||
    norm.includes('pre_press') ||
    norm.includes('prep')
  ) {
    return 'TRUOC_IN';
  }

  // 2.2. Sau in / Xử lý bề mặt (kiểm tra trước In ấn để tránh dính từ "in" trong "sau in")
  if (
    norm.includes('can mang') ||
    norm.includes('can') ||
    norm.includes('phu uv') ||
    norm.includes('uv bong') ||
    norm.includes('uv mo') ||
    norm.includes('uv cat') ||
    norm.includes('uv dinh hinh') ||
    norm.includes('vecni') ||
    norm.includes('vanish') ||
    norm.includes('ep kim') ||
    norm.includes('ep nhu') ||
    norm.includes('trang phu') ||
    norm.includes('phu mo') ||
    norm.includes('sau in') ||
    norm.includes('post_press') ||
    stepId.startsWith('uv') ||
    stepId.startsWith('can_')
  ) {
    return 'SAU_IN';
  }

  // 2.3. Thành phẩm & Đóng gói (kiểm tra trước In ấn để tránh dính từ "in" trong "finish")
  if (
    norm.includes('be') ||
    norm.includes('dan') ||
    norm.includes('gap') ||
    norm.includes('boi') ||
    norm.includes('cat tp') ||
    norm.includes('cat thanh pham') ||
    norm.includes('dong goi') ||
    norm.includes('dong thung') ||
    norm.includes('kcs') ||
    norm.includes('kiem tra') ||
    norm.includes('xo day') ||
    norm.includes('xe hop') ||
    norm.includes('bo') ||
    norm.includes('dan decal') ||
    norm.includes('thanh pham') ||
    stepId.startsWith('be_') ||
    stepId.startsWith('dan_') ||
    stepId === 'be' ||
    stepId === 'finish' ||
    stepId.includes('finish')
  ) {
    return 'THANH_PHAM';
  }

  // 2.4. In ấn
  if (
    norm.includes('in offset') ||
    norm.includes('in flexo') ||
    norm.includes('in lua') ||
    norm.includes('keo lua') ||
    norm.includes('in lot') ||
    norm.includes('in an') ||
    norm.includes('mau pha') ||
    norm.includes('cmyk') ||
    norm.includes('offset') ||
    norm.includes('flexo') ||
    norm.startsWith('in ') ||
    stepId.startsWith('in_') ||
    stepId === 'in' ||
    stepId === 'print'
  ) {
    return 'IN';
  }

  return 'KHAC';
}

/**
 * Sắp xếp danh sách công đoạn theo trình tự quy trình sản xuất ISO
 * Trước in -> In ấn -> Sau in -> Thành phẩm -> Khác
 */
export function sortProcessesByGroupAndOrder<T extends {
  group?: string | null;
  name?: string | null;
  defaultMachineType?: string | null;
  stepId?: string | null;
  sortOrder?: number | null;
}>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const groupA = resolveProcessGroup(a);
    const groupB = resolveProcessGroup(b);
    const orderDiff = PROCESS_GROUP_ORDER[groupA] - PROCESS_GROUP_ORDER[groupB];
    if (orderDiff !== 0) return orderDiff;

    const sortOrderA = a.sortOrder ?? 99;
    const sortOrderB = b.sortOrder ?? 99;
    if (sortOrderA !== sortOrderB) return sortOrderA - sortOrderB;

    return (a.name || '').localeCompare(b.name || '', 'vi');
  });
}

export interface EquipmentProcessGroupMeta {
  code: 'CAT_XEN' | 'IN_OFFSET_FLEXO' | 'CAN_MANG_TRANG_PHU' | 'BE_DAN_TP' | 'KHAC';
  stageKey: ProcessGroupKey;
  stageName: string;   // "Trước in", "In ấn", "Sau in", "Thành phẩm"
  name: string;        // Nhóm thiết bị: "Cắt xén", "In Offset/Flexo", "Cán màng/Tráng phủ", "Bế dán thành phẩm"
  detailName: string;  // Chi tiết thiết bị/công đoạn: "Cắt xén", "In Offset", "Cán màng", "Bế định hình", "Gấp dán hộp"...
  color: string;       // Tailwind badge classes
  dotColor: string;    // Tailwind dot color class
}

/**
 * Phân loại công đoạn theo 4 Nhóm Thiết Bị Thực Tế:
 * 1. Cắt xén (Trước in / Chuẩn bị phôi & CTP)
 * 2. In Offset/Flexo (In ấn các công nghệ)
 * 3. Cán màng/Tráng phủ (Sau in / Xử lý bề mặt)
 * 4. Bế dán thành phẩm (Hoàn thiện / Đóng gói)
 */
export function resolveEquipmentProcessGroup(item?: {
  group?: string | null;
  name?: string | null;
  defaultMachineType?: string | null;
  stepId?: string | null;
} | null): EquipmentProcessGroupMeta {
  const stageKey = resolveProcessGroup(item);
  const name = (item?.name || '').toLowerCase();
  const machine = (item?.defaultMachineType || '').toLowerCase();
  const stepId = (item?.stepId || '').toLowerCase();
  const text = `${name} ${machine} ${stepId}`;
  const norm = removeAccents(text);

  switch (stageKey) {
    case 'TRUOC_IN': {
      let detailName = 'Cắt xén';
      if (norm.includes('ghep') || norm.includes('laminat')) {
        detailName = 'Ghép màng';
      } else if (norm.includes('kem') || norm.includes('ctp') || norm.includes('binh')) {
        detailName = 'Bình bản / CTP';
      } else if (norm.includes('thiet ke')) {
        detailName = 'Thiết kế';
      } else if (norm.includes('cat') || norm.includes('xen')) {
        detailName = 'Cắt xén';
      }
      return {
        code: 'CAT_XEN',
        stageKey: 'TRUOC_IN',
        stageName: 'Trước in',
        name: 'Cắt xén',
        detailName,
        color: 'bg-purple-50 text-purple-700 border-purple-200',
        dotColor: 'bg-purple-500',
      };
    }

    case 'IN': {
      let detailName = 'In Offset';
      if (norm.includes('flexo')) {
        detailName = 'In Flexo';
      } else if (norm.includes('lua') || norm.includes('luoi')) {
        detailName = 'In Lụa';
      } else if (norm.includes('kts') || norm.includes('ky thuat so') || norm.includes('digital')) {
        detailName = 'In KTS';
      } else {
        detailName = 'In Offset';
      }
      return {
        code: 'IN_OFFSET_FLEXO',
        stageKey: 'IN',
        stageName: 'In ấn',
        name: 'In Offset/Flexo',
        detailName,
        color: 'bg-blue-50 text-blue-700 border-blue-200',
        dotColor: 'bg-blue-500',
      };
    }

    case 'SAU_IN': {
      let detailName = 'Cán màng/Tráng phủ';
      if (norm.includes('can') || norm.includes('giut mang') || norm.includes('laminat')) {
        detailName = 'Cán màng';
      } else if (norm.includes('uv') || norm.includes('vecni') || norm.includes('vanish') || norm.includes('trang') || norm.includes('phu')) {
        detailName = 'Tráng phủ / UV';
      } else if (norm.includes('ep kim') || norm.includes('ep nhu') || norm.includes('nhu') || norm.includes('hot_foil')) {
        detailName = 'Ép kim / Ép nhũ';
      } else if (norm.includes('can van') || norm.includes('van')) {
        detailName = 'Cán vân';
      }
      return {
        code: 'CAN_MANG_TRANG_PHU',
        stageKey: 'SAU_IN',
        stageName: 'Sau in',
        name: 'Cán màng/Tráng phủ',
        detailName,
        color: 'bg-amber-50 text-amber-700 border-amber-200',
        dotColor: 'bg-amber-500',
      };
    }

    case 'THANH_PHAM': {
      let detailName = 'Bế dán thành phẩm';
      if (norm.includes('be noi') || norm.includes('thuc noi')) {
        detailName = 'Bế nổi';
      } else if (norm.includes('be')) {
        detailName = 'Bế định hình';
      } else if (norm.includes('dan') || norm.includes('gap')) {
        detailName = 'Gấp dán hộp';
      } else if (norm.includes('boi')) {
        detailName = 'Bồi sóng';
      } else if (norm.includes('cat') || norm.includes('te') || norm.includes('xen tp')) {
        detailName = 'Cắt xén TP';
      } else if (norm.includes('dong goi') || norm.includes('dong thung') || norm.includes('bo')) {
        detailName = 'Đóng gói';
      } else if (norm.includes('kcs') || norm.includes('kiem')) {
        detailName = 'KCS / Kiểm hàng';
      } else if (norm.includes('xo day') || norm.includes('xe hop')) {
        detailName = 'Hoàn thiện thủ công';
      } else if (norm.includes('mat cao')) {
        detailName = 'Đóng mặt cao';
      }
      return {
        code: 'BE_DAN_TP',
        stageKey: 'THANH_PHAM',
        stageName: 'Thành phẩm',
        name: 'Bế dán thành phẩm',
        detailName,
        color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        dotColor: 'bg-emerald-500',
      };
    }

    default:
      return {
        code: 'KHAC',
        stageKey: 'KHAC',
        stageName: 'Khác',
        name: 'Khác',
        detailName: 'Khác',
        color: 'bg-slate-50 text-slate-700 border-slate-200',
        dotColor: 'bg-slate-400',
      };
  }
}

